import { useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useRef, useState } from 'react'

import { getRealtimeRef } from '@/integrations/firebase'

import { INDIVIDUAL_INVALIDATION_KEYS } from './regen-invalidation'

export type SubscriptionRealtime = {
  status?: 'trial' | 'active' | 'expired'
}

/**
 * Live subscription status from RTDB.
 *
 * Unlike regen — which the backend mirrors from its own service layer — this node is
 * written by a Payload collection hook, because subscription state is changed by hand
 * in the admin UI and never passes through a service. That's the whole reason this
 * listener exists: without it a state flip wouldn't reach an open tab until something
 * else happened to refetch.
 *
 * Returns undefined until the first snapshot; callers fall back to the value on the
 * individual response. RTDB is never authoritative here — the backend re-reads
 * entitlement from Payload on every request.
 */
export const useSubscriptionRealtime = (individualId?: string) => {
  const [value, setValue] = useState<SubscriptionRealtime>()
  const queryClient = useQueryClient()
  const previousStatus = useRef<SubscriptionRealtime['status']>(undefined)

  useEffect(() => {
    setValue(undefined)
    previousStatus.current = undefined
    if (!individualId) return

    const ref = getRealtimeRef(`individuals/${individualId}/subscription`)

    return onValue(ref, snapshot => {
      const next = snapshot.exists() ? (snapshot.val() as SubscriptionRealtime) : undefined
      setValue(next)

      // Refetch the individual on a real transition so the derived numbers
      // (documentsRemaining, documentLimit) come from the server rather than being
      // recomputed here. Skipped on the first snapshot, which only tells us what we
      // already had.
      const changed =
        previousStatus.current !== undefined && next?.status !== previousStatus.current

      if (changed) {
        for (const key of INDIVIDUAL_INVALIDATION_KEYS) {
          queryClient.invalidateQueries({ queryKey: [key] })
        }
      }

      previousStatus.current = next?.status
    })
  }, [individualId, queryClient])

  return value
}
